import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Text from "../../../../Diathrive/src/components/elements/Text";
import Button from "../../../../Diathrive/src/components/elements/Button";
import { BUTTON_TYPES, COLORS } from "../../../../Diathrive/src/utils/Constants";
import { fetchApi } from "../../../../Diathrive/src/utils/ApiHelper";
import { HTTP_METHODS } from "../../../../Diathrive/src/utils/URLConstants";
import styles from "./styles";

const DateTimeSelectWeb = ({ eventType, onNext, onBack }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);

  const weekStart = useMemo(
    () => moment().startOf("day").add(weekOffset, "weeks"),
    [weekOffset]
  );
  const weekEnd = useMemo(() => moment(weekStart).add(7, "days"), [weekStart]);
  const days = useMemo(() => {
    const result = [];
    for (let i = 0; i < 7; i++) {
      result.push(moment(weekStart).add(i, "days"));
    }
    return result;
  }, [weekStart]);

  useEffect(() => {
    setLoading(true);
    setSelectedDate(null);
    setSelectedTime(null);
    // Calendly rejects start_time in the past; add 1min buffer for current week
    const apiStart = weekOffset === 0 ? moment().add(1, "minute").toISOString() : weekStart.toISOString();
    const params = `?event_type=${encodeURIComponent(eventType.uri)}&start_time=${apiStart}&end_time=${weekEnd.toISOString()}`;
    fetchApi(HTTP_METHODS.GET, "patients/calendly/available-times" + params)
      .then((response) => {
        setAvailableTimes(response.data.data.available_times || []);
      })
      .catch(() => setAvailableTimes([]))
      .finally(() => setLoading(false));
  }, [weekOffset]);

  const timesForDate = useMemo(() => {
    if (!selectedDate) return [];
    return availableTimes.filter((slot) => {
      const slotDate = moment(slot.start_time).format("YYYY-MM-DD");
      return slotDate === selectedDate.format("YYYY-MM-DD") && slot.status === "available";
    });
  }, [availableTimes, selectedDate]);

  return (
    <div style={styles.container}>
      <Text variation="Text2Medium" color={COLORS.grey2}>
        {eventType.name} with {eventType.advisorName}
      </Text>

      {/* Week nav */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, marginBottom: 16 }}>
        <Button
          buttonType={BUTTON_TYPES.Bordered}
          title="< Prev"
          onPress={() => setWeekOffset(Math.max(0, weekOffset - 1))}
          disabled={weekOffset === 0}
          style={{ maxWidth: 100 }}
        />
        <Text variation="Subtitle2Bold" color={COLORS.black}>
          {weekStart.format("MMM D")} -{" "}
          {moment(weekEnd).subtract(1, "day").format("MMM D, YYYY")}
        </Text>
        <Button
          buttonType={BUTTON_TYPES.Bordered}
          title="Next >"
          onPress={() => setWeekOffset(weekOffset + 1)}
          style={{ maxWidth: 100 }}
        />
      </div>

      {/* Day buttons */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {days.map((day) => {
          const isSelected = selectedDate && day.format("YYYY-MM-DD") === selectedDate.format("YYYY-MM-DD");
          const isPast = day.isBefore(moment(), "day");
          return (
            <div
              key={day.format("YYYY-MM-DD")}
              onClick={() => { if (!isPast) { setSelectedDate(day); setSelectedTime(null); } }}
              style={{
                flex: 1,
                textAlign: "center",
                padding: "8px 4px",
                borderRadius: 12,
                cursor: isPast ? "default" : "pointer",
                opacity: isPast ? 0.4 : 1,
                backgroundColor: isSelected ? COLORS.pink : "transparent",
              }}
            >
              <Text variation="Text2Medium" color={isSelected ? COLORS.white : COLORS.grey2}>
                {day.format("ddd")}
              </Text>
              <Text variation="Body1SemiBold" color={isSelected ? COLORS.white : COLORS.black}>
                {day.format("D")}
              </Text>
            </div>
          );
        })}
      </div>

      {/* Time slots */}
      {loading ? (
        <Text variation="Text1Regular" color={COLORS.grey2} topMargin={24}>
          Loading available times...
        </Text>
      ) : !selectedDate ? (
        <Text variation="Text1Regular" color={COLORS.grey2} topMargin={24}>
          Select a day to see available times
        </Text>
      ) : timesForDate.length === 0 ? (
        <Text variation="Text1Regular" color={COLORS.grey2} topMargin={24}>
          No available times on this day
        </Text>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {timesForDate.map((slot) => {
            const isSelected = selectedTime?.start_time === slot.start_time;
            return (
              <div
                key={slot.start_time}
                onClick={() => setSelectedTime(slot)}
                style={{
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: `1px solid ${isSelected ? COLORS.pink : COLORS.greyLight || "#E0E4ED"}`,
                  backgroundColor: isSelected ? COLORS.pink : COLORS.white,
                  cursor: "pointer",
                  minWidth: 100,
                  textAlign: "center",
                }}
              >
                <Text variation="Body1SemiBold" color={isSelected ? COLORS.white : COLORS.black}>
                  {moment(slot.start_time).format("h:mm A")}
                </Text>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ ...styles.typeSelectButtons, marginTop: 32 }}>
        <Button
          style={{ flex: 1 }}
          buttonType={BUTTON_TYPES.Bordered}
          title="Back"
          onPress={onBack}
        />
        <Button
          disabled={!selectedTime}
          style={{ flex: 1 }}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={COLORS.white}
          title="Next"
          onPress={() => onNext(selectedTime.start_time)}
        />
      </div>
    </div>
  );
};

export default DateTimeSelectWeb;
